import React, { useState } from "react";
import {
  Drawer,
  Input,
  Checkbox,
  List,
  message,
  Spin,
  Avatar,
  Tag,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import {
  drawerTitle,
  drawerFooter,
  DRAWER_WIDE_WIDTH,
} from "../../../components/AppUi/appUi";
import {
  useProductsQuery,
  useToggleProductIsOnWebMutation,
} from "../../../Services";
import { Product } from "../../../Types";
import { IMAGE_URL } from "../../../Constant";

const getProductImage = (image_paths: string): string | null => {
  try {
    const imgs = JSON.parse(image_paths || "[]");
    if (imgs.length > 0) return `${IMAGE_URL}/${imgs[0]}`;
  } catch {}
  return null;
};

interface WebProductsDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const WebProductsDrawer: React.FC<WebProductsDrawerProps> = ({
  open,
  onClose,
}) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const productsQuery = useProductsQuery(
    { page, limit: 20, search },
    { enabled: open },
  );
  const toggleMutation = useToggleProductIsOnWebMutation();

  const products = productsQuery.data?.data ?? [];
  const total = productsQuery.data?.total ?? 0;

  const handleToggle = (product: Product) => {
    const newStatus = product.is_on_web ? 0 : 1;
    toggleMutation.mutate(
      { id: product.id, is_on_web: newStatus as 0 | 1 },
      {
        onSuccess: (res) => {
          if (res?.success) {
            message.success(
              `${product.title} ${newStatus ? "added to" : "removed from"} web`,
            );
          }
        },
      },
    );
  };

  return (
    <Drawer
      title={drawerTitle(
        "Web Products",
        "Toggle which products appear on the website",
      )}
      open={open}
      onClose={onClose}
      placement="right"
      width={DRAWER_WIDE_WIDTH}
      destroyOnClose
      footer={drawerFooter({
        actions: [{ label: "Close", onClick: onClose }],
      })}
    >
      <Input
        placeholder="Search products..."
        prefix={<SearchOutlined />}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
        className="!mb-4"
        allowClear
      />

      <Spin spinning={productsQuery.isLoading}>
        <List
          dataSource={products}
          pagination={
            total > 20
              ? {
                  current: page,
                  pageSize: 20,
                  total,
                  onChange: setPage,
                  size: "small",
                  showSizeChanger: false,
                }
              : false
          }
          renderItem={(product: Product) => (
            <List.Item
              className="!px-2 !py-2 hover:!bg-gray-50 rounded transition-colors"
              actions={[
                <Checkbox
                  key="toggle"
                  checked={!!product.is_on_web}
                  onChange={() => handleToggle(product)}
                />,
              ]}
            >
              <List.Item.Meta
                avatar={
                  <Avatar
                    src={getProductImage(product.image_paths)}
                    size={40}
                    shape="square"
                    className="!rounded"
                  >
                    {product.title?.[0]}
                  </Avatar>
                }
                title={
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{product.title}</span>
                    {product.is_on_web ? (
                      <Tag color="green" className="!m-0 !text-[10px]">
                        On Web
                      </Tag>
                    ) : null}
                  </div>
                }
                description={
                  <span className="text-xs text-gray-400">
                    {product.sku_code}
                    {product.category_name ? ` · ${product.category_name}` : ""}
                    {product.brand_name ? ` · ${product.brand_name}` : ""}
                  </span>
                }
              />
            </List.Item>
          )}
        />
      </Spin>
    </Drawer>
  );
};
